import { suggestedAnswerButtonInViewQueryKey } from '@app/src/pages/Questionnaire/Provider/Report/SuggestedAnswers/suggestionState'
import { useQueryClient } from '@tanstack/react-query'
import { useConfirm } from 'material-ui-confirm'
import React from 'react'
import { useIntl } from 'react-intl'
import endpoints from '../api/endpoints'
import { FetchKey } from '../api/fetchHooks'
import { useCreateResource } from '../api/updateHooks'
import { useAccount } from '../context/AccountContext'
import { useAmplitude } from '../context/AmplitudeContext'
import { useSnackbar } from '../context/SnackbarContext'
import { consumePrepareCardSource } from '../utils/prepareCardTracking'
import { br } from '../utils/translationMarkup'
import { AmplitudeTrackingEvents, NotificationSeverity, PrepareCardAction, PrepareCardId } from '../wf-constants'
import RequestError from '../errors/RequestError'
import useErrorNotification from './errorNotification'
import { useRepositoryFileReplacement } from './useRepositoryFileReplacement'

export const useAiSuggestionsUploadFile = () => {
  const [isWaitingToReload, setIsWaitingToReload] = React.useState(false)
  const [isCheckingFileName, setIsCheckingFileName] = React.useState(false)
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()
  const { formatMessage } = useIntl()
  const confirm = useConfirm()
  const { showSnackbar } = useSnackbar()
  const { trackEvent } = useAmplitude()
  const { account } = useAccount()
  const { mutateAsync: uploadFileAsync, isPending: isUploading } = useCreateResource<unknown, FormData>()
  const { fetchRepositoryFileNames, confirmReplacing } = useRepositoryFileReplacement()

  const shouldUploadFile = async (file: File) => {
    setIsCheckingFileName(true)
    try {
      const repositoryFileNames = await fetchRepositoryFileNames()
      return !repositoryFileNames.has(file.name) || (await confirmReplacing([file.name]))
    } catch (error) {
      showErrorNotification({
        requestError: error instanceof RequestError ? error : null,
        specificFallbackErrorMessage: formatMessage({ id: 'reporting.uploadFileError' }),
      })
      return false
    } finally {
      setIsCheckingFileName(false)
    }
  }

  const uploadFile = async (file: File) => {
    if (!(await shouldUploadFile(file))) return

    const formData = new FormData()
    formData.append('file', file)
    await uploadFileAsync(
      { url: endpoints.suggestionFiles, body: formData },
      {
        onSuccess: () => {
          setIsWaitingToReload(true)
          showSnackbar({
            message: formatMessage({ id: 'resourceExplorer.activity.successfullyUploadedFile' }),
            severity: NotificationSeverity.success,
          })
          trackEvent({
            name: AmplitudeTrackingEvents.Transparency.AiFileRepository.FileUploaded,
            eventProps: {
              org_id: account?.organization?.id,
            },
          })
          if (consumePrepareCardSource(PrepareCardId.Import)) {
            trackEvent({
              name: AmplitudeTrackingEvents.Dashboard.PrepareForRequest.CardActionCompleted,
              eventProps: { card_id: PrepareCardId.Import, action: PrepareCardAction.FileUploaded },
            })
          }
          queryClient.invalidateQueries({ queryKey: [suggestedAnswerButtonInViewQueryKey] })
          setTimeout(() => {
            queryClient.resetQueries({ queryKey: [FetchKey.SuggestedResponse] })
            queryClient.invalidateQueries({ queryKey: [FetchKey.SuggestedFiles] })
            queryClient.invalidateQueries({ queryKey: [FetchKey.FileRepository] })
            setIsWaitingToReload(false)
          }, 2000)
        },
        onError: error => {
          if (error.statusCode === 403 && error.body?.message?.toLowerCase() === 'file extension not allowed') {
            confirm({
              title: formatMessage({ id: 'suggestedAnswers.fileFormatNotSupportedHeader' }),
              content: formatMessage({ id: 'suggestedAnswers.fileFormatNotSupportedText' }, { br }),
              confirmationText: formatMessage({ id: 'general.close' }),
              confirmationButtonProps: { variant: 'text' },
              hideCancelButton: true,
            })
            return
          }
          showErrorNotification({
            requestError: error,
            specificFallbackErrorMessage: formatMessage({ id: 'reporting.uploadFileError' }),
          })
        },
      },
    )
    formData.delete('file')
  }

  return { isWaitingToReload, isUploading: isUploading || isCheckingFileName, uploadFile }
}
